/**
 * Flutter Bridge - Communication layer between React WebView and Flutter native shell
 */

interface BridgeRequest {
  id: string
  action: string
  payload: Record<string, unknown>
}

interface BridgeResponse {
  id: string
  success: boolean
  data?: Record<string, unknown>
  error?: {
    code: string
    message: string
  }
}

interface BridgeEvent {
  event: string
  data: Record<string, unknown>
}

interface PendingRequest {
  resolve: (data: Record<string, unknown>) => void
  reject: (error: Error) => void
  timeout: ReturnType<typeof setTimeout>
}

interface AppInfo {
  platform: 'ios' | 'android'
  version: string
  buildNumber: string
  packageName: string
}

// Extend Window interface
declare global {
  interface Window {
    FlutterBridge?: {
      postMessage: (message: string) => void
      receive?: (response: string) => void
      onEvent?: (event: string) => void
    }
    isFlutterApp?: boolean
  }
}

class FlutterBridgeClient {
  private pendingRequests = new Map<string, PendingRequest>()
  private eventListeners = new Map<string, Set<(data: Record<string, unknown>) => void>>()
  private _isReady = false

  constructor() {
    this.setupBridge()
  }

  private setupBridge() {
    // Set up receiver methods for Flutter to call
    if (typeof window !== 'undefined') {
      // Initialize FlutterBridge if it doesn't exist
      if (!window.FlutterBridge) {
        window.FlutterBridge = {
          postMessage: () => {},
        }
      }

      // Add receive method for responses
      window.FlutterBridge.receive = this.handleResponse.bind(this)

      // Add event handler for push events
      window.FlutterBridge.onEvent = this.handleEvent.bind(this)

      // Listen for Flutter ready event
      window.addEventListener('flutter:ready', () => {
        this._isReady = true
      })

      // Check if already in Flutter
      if (window.isFlutterApp) {
        this._isReady = true
      }
    }
  }

  /**
   * Check if running inside Flutter WebView
   */
  get isInFlutter(): boolean {
    return (
      typeof window !== 'undefined' &&
      (window.isFlutterApp === true ||
        // Check for Flutter's JavaScript channel
        (window.FlutterBridge !== undefined &&
          typeof window.FlutterBridge.postMessage === 'function' &&
          window.FlutterBridge.postMessage.toString().includes('[native code]')))
    )
  }

  get isReady(): boolean {
    return this._isReady
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
  }

  private send<T>(
    action: string,
    payload: Record<string, unknown> = {},
    timeoutMs = 30000
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.isInFlutter) {
        reject(new Error('Not running in Flutter WebView'))
        return
      }

      const id = this.generateId()
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(id)
        reject(new Error(`Bridge request timeout: ${action}`))
      }, timeoutMs)

      this.pendingRequests.set(id, {
        resolve: resolve as (data: Record<string, unknown>) => void,
        reject,
        timeout,
      })

      const request: BridgeRequest = { id, action, payload }

      try {
        window.FlutterBridge?.postMessage(JSON.stringify(request))
      } catch (e) {
        this.pendingRequests.delete(id)
        clearTimeout(timeout)
        reject(new Error(`Failed to send bridge message: ${e}`))
      }
    })
  }

  private handleResponse(responseJson: string) {
    try {
      const response: BridgeResponse = JSON.parse(responseJson)
      const pending = this.pendingRequests.get(response.id)

      if (pending) {
        clearTimeout(pending.timeout)
        this.pendingRequests.delete(response.id)

        if (response.success) {
          pending.resolve(response.data || {})
        } else {
          pending.reject(new Error(response.error?.message || 'Bridge error'))
        }
      }
    } catch (e) {
      console.error('Failed to parse bridge response:', e)
    }
  }

  private handleEvent(eventJson: string) {
    try {
      const { event, data }: BridgeEvent = JSON.parse(eventJson)
      const listeners = this.eventListeners.get(event)
      listeners?.forEach((listener) => listener(data))
    } catch (e) {
      console.error('Failed to parse bridge event:', e)
    }
  }

  // Public API

  /**
   * Get FCM push notification token
   */
  async getFcmToken(): Promise<string | null> {
    const result = await this.send<{ token: string | null }>('getFcmToken')
    return result.token
  }

  /**
   * Store a value in secure storage (encrypted)
   */
  async storeSecure(key: string, value: string): Promise<void> {
    await this.send('storeSecure', { key, value })
  }

  /**
   * Get a value from secure storage
   */
  async getSecure(key: string): Promise<string | null> {
    const result = await this.send<{ value: string | null }>('getSecure', { key })
    return result.value
  }

  /**
   * Delete a value from secure storage
   */
  async deleteSecure(key: string): Promise<void> {
    await this.send('deleteSecure', { key })
  }

  /**
   * Open URL in external browser
   */
  async openExternal(url: string): Promise<void> {
    await this.send('openExternal', { url })
  }

  /**
   * Get app information (platform, version, etc.)
   */
  async getAppInfo(): Promise<AppInfo> {
    return this.send<AppInfo>('getAppInfo')
  }

  /**
   * Get available subscription products with localized prices
   */
  async getSubscriptionProducts(): Promise<{ products: Array<{ id: string; title: string; description: string; price: string; currencyCode: string }> }> {
    return this.send('getSubscriptionProducts')
  }

  /**
   * Start a native purchase flow
   */
  async purchaseSubscription(productId: string): Promise<{ started: boolean }> {
    return this.send('purchaseSubscription', { productId })
  }

  /**
   * Restore previous purchases (required by Apple)
   */
  async restorePurchases(): Promise<void> {
    await this.send('restorePurchases')
  }

  /**
   * Subscribe to events from Flutter
   */
  onEvent(event: string, listener: (data: Record<string, unknown>) => void): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)!.add(listener)

    // Return unsubscribe function
    return () => this.eventListeners.get(event)?.delete(listener)
  }

  /**
   * Wait for bridge to be ready
   */
  waitForReady(timeoutMs = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this._isReady) {
        resolve()
        return
      }

      const timeout = setTimeout(() => {
        reject(new Error('Flutter bridge ready timeout'))
      }, timeoutMs)

      const handleReady = () => {
        clearTimeout(timeout)
        window.removeEventListener('flutter:ready', handleReady)
        resolve()
      }

      window.addEventListener('flutter:ready', handleReady)
    })
  }
}

// Export singleton instance
export const flutterBridge = new FlutterBridgeClient()
